// AutoShell · shell PERSISTENTE del área de miembros Robotea Auto (/auto/*).
// ---------------------------------------------------------------------------
// HERMANO de CampusShell (layout-route de react-router, registrado al mismo
// nivel en App.tsx), NUNCA anidado dentro de él. Robotea Auto es un producto
// premium aparte ("gestión hágalo-por-mí", no formación) y debe SENTIRSE así:
// chrome propio, nav propia (AUTO_NAV) y estética con la firma --sig-auto.
//
// FAIL-CLOSED (mismo contrato que auto-access.ts): el panel solo se muestra
// cuando `hasAuto(enrolledSlugs)` confirma el entitlement 'robotea-auto'.
// Cualquier otro caso —sin entitlement O fetch de cursos fallido— redirige a
// /auto/activar (la venta). La seguridad REAL es server-side; esto controla
// qué se MUESTRA (deny-by-default).
//
// Owner: Carlos Beltrán · 2026-07-08 · F-CAMPUS-AUTO-ACCESS-001

import { Suspense, useEffect, useState } from 'react';
import { Link, Navigate, NavLink, Outlet } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { useAuth } from '../lib/auth-context';
import { fetchMyCourses } from '../lib/api';
import { hasAuto } from '../lib/auto-access';
import { AUTO_NAV, AUTO_NAV_IMPLEMENTED } from '../lib/appshell-auto-nav';

// Estado del gate de entitlement. 'checking' mientras resolvemos los cursos;
// 'granted' solo con confirmación positiva; 'denied' en el resto (fail-closed).
type Gate = 'checking' | 'granted' | 'denied';

function GateSpinner(): JSX.Element {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-screen items-center justify-center bg-canvas"
    >
      <div className="text-center">
        <div
          aria-hidden="true"
          className="mx-auto mb-3 h-7 w-7 animate-spin rounded-full border-2 border-ink/15 border-t-[var(--sig-auto)]"
        />
        <p className="text-sm text-ink/50">Abriendo tu panel Auto…</p>
      </div>
    </div>
  );
}

/** Ítem de la nav lateral. Solo las rutas implementadas navegan (NavLink);
 *  el resto se pinta como "Pronto" sin enlace muerto → sin 404. */
function AutoNavItem({
  to,
  label,
  icon: Icon,
}: {
  to: string;
  label: string;
  icon: (typeof AUTO_NAV)[number]['icon'];
}): JSX.Element {
  if (!AUTO_NAV_IMPLEMENTED.has(to)) {
    return (
      <span
        aria-disabled="true"
        title="Disponible próximamente"
        className="flex cursor-default items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-[var(--sb-ink-mute)]"
      >
        <Icon aria-hidden="true" className="h-[18px] w-[18px] shrink-0 opacity-70" />
        <span className="flex-1 truncate">{label}</span>
        <span className="rounded-full border border-[var(--sb-hairline)] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--sb-ink-mute)]">
          Pronto
        </span>
      </span>
    );
  }
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        [
          'flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition',
          isActive
            ? 'bg-[var(--sig-auto)]/15 text-[var(--sig-auto)] shadow-[inset_0_0_0_1px] shadow-[var(--sig-auto)]/25'
            : 'text-[var(--sb-ink-soft)] hover:bg-[var(--sb-surface-2)] hover:text-[var(--sb-ink)]',
        ].join(' ')
      }
    >
      <Icon aria-hidden="true" className="h-[18px] w-[18px] shrink-0" />
      <span className="flex-1 truncate">{label}</span>
    </NavLink>
  );
}

export default function AutoShell(): JSX.Element {
  const { state } = useAuth();
  const auth = state.status === 'authenticated' ? state.identity : null;
  const [gate, setGate] = useState<Gate>('checking');

  useEffect(() => {
    if (!auth) {
      setGate('checking');
      return;
    }
    let active = true;
    fetchMyCourses(auth)
      .then((mine) => {
        if (!active) return;
        setGate(hasAuto(mine.enrolled.map((c) => c.slug)) ? 'granted' : 'denied');
      })
      .catch(() => {
        // Fail-closed: sin confirmación de entitlement NO se muestra el panel.
        if (active) setGate('denied');
      });
    return () => {
      active = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- auth?.email is a stable scalar; re-running on every auth object reference would cause unnecessary fetchMyCourses calls on each render
  }, [auth?.email]);

  // Sesión aún cargando o inexistente → delegamos en el Outlet: el RequireAuth
  // de la ruta hija muestra su spinner o redirige a /login (mismo contrato que
  // CampusShell, que tampoco monta chrome sin sesión confirmada).
  if (!auth) {
    return <Outlet />;
  }

  if (gate === 'checking') {
    return <GateSpinner />;
  }

  if (gate === 'denied') {
    // FAIL-CLOSED · sin entitlement el panel no existe para este usuario.
    return <Navigate to="/auto/activar" replace />;
  }

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-canvas text-ink">
      <div className="flex min-h-0 flex-1">
        {/* ── Sidebar propia (gestión premium · firma --sig-auto) ─────────── */}
        <aside className="hidden w-[264px] shrink-0 flex-col border-r border-[var(--sb-hairline)] bg-[var(--sb-rail)] md:flex">
          {/* Marca del área */}
          <div className="border-b border-[var(--sb-hairline)] px-5 pb-4 pt-5">
            <Link to="/auto" className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[var(--sig-auto)]/15 text-[var(--sig-auto)]">
                <Sparkles aria-hidden="true" className="h-5 w-5" />
              </span>
              <span className="leading-tight">
                <span className="block text-sm font-bold tracking-tight text-[var(--sb-ink)]">
                  Robotea <span className="text-[var(--sig-auto)]">Auto</span>
                </span>
                <span className="block text-[11px] font-medium uppercase tracking-wider text-[var(--sb-ink-mute)]">
                  Gestión premium
                </span>
              </span>
            </Link>
          </div>

          <nav aria-label="Navegación Robotea Auto" className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
            {AUTO_NAV.map((item) => (
              <AutoNavItem key={item.to} to={item.to} label={item.label} icon={item.icon} />
            ))}
          </nav>

          {/* Pie · vuelta al campus del curso (mundo separado, puente explícito) */}
          <div className="border-t border-[var(--sb-hairline)] px-3 py-3">
            <Link
              to="/dashboard"
              className="flex items-center gap-2 rounded-xl px-3 py-2 text-xs font-medium text-[var(--sb-ink-mute)] transition hover:bg-[var(--sb-surface-2)] hover:text-[var(--sb-ink)]"
            >
              <ArrowLeft aria-hidden="true" className="h-3.5 w-3.5" />
              Volver al campus
            </Link>
          </div>
        </aside>

        {/* ── Contenido ────────────────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-1 flex-col">
          {/* Barra superior móvil (la sidebar se oculta en <md) */}
          <header className="flex items-center justify-between border-b border-[var(--sb-hairline)] bg-[var(--sb-rail)] px-4 py-3 md:hidden">
            <Link to="/auto" className="flex items-center gap-2">
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--sig-auto)]/15 text-[var(--sig-auto)]">
                <Sparkles aria-hidden="true" className="h-4 w-4" />
              </span>
              <span className="text-sm font-bold tracking-tight text-[var(--sb-ink)]">
                Robotea <span className="text-[var(--sig-auto)]">Auto</span>
              </span>
            </Link>
            <Link to="/dashboard" className="text-xs font-medium text-[var(--sb-ink-mute)] hover:text-[var(--sb-ink)]">
              Campus
            </Link>
          </header>

          <main className="min-h-0 flex-1 overflow-y-auto">
            {/* Boundary DENTRO del shell (patrón CampusShell anti-parpadeo):
                al navegar entre secciones Auto el chrome persiste. */}
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          </main>
        </div>
      </div>
    </div>
  );
}
